// Self-contained math expression parser/evaluator and function plot SVG generator.
// Grammar (must stay in sync with DETECT_FUNCTION_PROMPT in services/aiService.js):
//   Expr  := Term (('+'|'-') Term)*
//   Term  := Unary (('*'|'/') Unary)*
//   Unary := ('-'|'+') Unary | Power
//   Power := Atom ('^' Unary)?          right-associative; -x^2 parses as -(x^2)
//   Atom  := NUMBER | 'x' | 'y' | 'pi' | 'e' | FUNC '(' Expr ')' | '(' Expr ')'
//   FUNC  := sin | cos | tan | sqrt | log | ln | exp | abs   (log = base 10, ln = natural)
// An expression using 'y' is a surface z = f(x,y) and renders as a 3D plot;
// evaluate() takes (node, x, y) — the y argument only matters for surfaces.
// No imports: shared by StickyElements.js, drawUtils.js and FunctionGraphEditDialog.js.

const functionPlot = {};

const FUNCTIONS = {
    sin: Math.sin,
    cos: Math.cos,
    tan: Math.tan,
    sqrt: Math.sqrt,
    log: Math.log10,
    ln: Math.log,
    exp: Math.exp,
    abs: Math.abs,
};

const CONSTANTS = {
    pi: Math.PI,
    e: Math.E,
};

function tokenize(str) {
    const tokens = [];
    let i = 0;
    while (i < str.length) {
        const c = str[i];
        if (c === ' ' || c === '\t' || c === '\n' || c === '\r') {
            i++;
        } else if (c >= '0' && c <= '9') {
            let j = i;
            while (j < str.length && str[j] >= '0' && str[j] <= '9') j++;
            if (str[j] === '.') {
                j++;
                while (j < str.length && str[j] >= '0' && str[j] <= '9') j++;
            }
            tokens.push({ type: 'number', value: parseFloat(str.slice(i, j)) });
            i = j;
        } else if (c >= 'a' && c <= 'z') {
            let j = i;
            while (j < str.length && str[j] >= 'a' && str[j] <= 'z') j++;
            const name = str.slice(i, j);
            // Note: no function name may ever contain the letter 'y' — the server
            // classifies 2d/3d by testing the expression for a 'y' character.
            if (name !== 'x' && name !== 'y' && !FUNCTIONS[name] && CONSTANTS[name] === undefined) {
                throw new Error('Unknown identifier: ' + name);
            }
            tokens.push({ type: 'name', value: name });
            i = j;
        } else if ('+-*/^()'.indexOf(c) >= 0) {
            tokens.push({ type: 'op', value: c });
            i++;
        } else {
            throw new Error('Unexpected character: ' + c);
        }
    }
    return tokens;
}

functionPlot.parse = function (str) {
    if (typeof str !== 'string' || str.trim().length === 0) {
        throw new Error('Empty expression');
    }
    const tokens = tokenize(str);
    let pos = 0;

    const peek = () => tokens[pos];
    const isOp = (value) => tokens[pos] && tokens[pos].type === 'op' && tokens[pos].value === value;

    function parseExpr() {
        let node = parseTerm();
        while (isOp('+') || isOp('-')) {
            const op = tokens[pos++].value;
            node = { type: 'binary', op, left: node, right: parseTerm() };
        }
        return node;
    }

    function parseTerm() {
        let node = parseUnary();
        while (isOp('*') || isOp('/')) {
            const op = tokens[pos++].value;
            node = { type: 'binary', op, left: node, right: parseUnary() };
        }
        return node;
    }

    function parseUnary() {
        if (isOp('-') || isOp('+')) {
            const op = tokens[pos++].value;
            const operand = parseUnary();
            return op === '-' ? { type: 'negate', operand } : operand;
        }
        return parsePower();
    }

    function parsePower() {
        const base = parseAtom();
        if (isOp('^')) {
            pos++;
            return { type: 'binary', op: '^', left: base, right: parseUnary() };
        }
        return base;
    }

    function parseAtom() {
        const token = peek();
        if (!token) throw new Error('Unexpected end of expression');
        if (token.type === 'number') {
            pos++;
            return { type: 'number', value: token.value };
        }
        if (token.type === 'name') {
            pos++;
            if (token.value === 'x' || token.value === 'y') return { type: 'variable', name: token.value };
            if (CONSTANTS[token.value] !== undefined) return { type: 'number', value: CONSTANTS[token.value] };
            if (!isOp('(')) throw new Error('Expected ( after ' + token.value);
            pos++;
            const arg = parseExpr();
            if (!isOp(')')) throw new Error('Expected ) after argument of ' + token.value);
            pos++;
            return { type: 'call', name: token.value, arg };
        }
        if (isOp('(')) {
            pos++;
            const node = parseExpr();
            if (!isOp(')')) throw new Error('Expected )');
            pos++;
            return node;
        }
        throw new Error('Unexpected token: ' + token.value);
    }

    const root = parseExpr();
    if (pos < tokens.length) throw new Error('Unexpected token: ' + tokens[pos].value);
    return root;
};

functionPlot.evaluate = function evaluate(node, x, y) {
    switch (node.type) {
        case 'number':
            return node.value;
        case 'variable':
            return node.name === 'y' ? y : x;
        case 'negate':
            return -evaluate(node.operand, x, y);
        case 'call':
            return FUNCTIONS[node.name](evaluate(node.arg, x, y));
        case 'binary': {
            const left = evaluate(node.left, x, y);
            const right = evaluate(node.right, x, y);
            switch (node.op) {
                case '+':
                    return left + right;
                case '-':
                    return left - right;
                case '*':
                    return left * right;
                case '/':
                    return left / right;
                case '^':
                    return Math.pow(left, right);
                default:
                    return NaN;
            }
        }
        default:
            return NaN;
    }
};

function usesY(node) {
    switch (node.type) {
        case 'variable':
            return node.name === 'y';
        case 'negate':
            return usesY(node.operand);
        case 'call':
            return usesY(node.arg);
        case 'binary':
            return usesY(node.left) || usesY(node.right);
        default:
            return false;
    }
}

functionPlot.validate = function (str) {
    try {
        const root = functionPlot.parse(str);
        return { valid: true, usesY: usesY(root) };
    } catch (err) {
        return { valid: false, error: err.message };
    }
};

// ---------------------------------------------------------------------------
// SVG generation
// ---------------------------------------------------------------------------

const VIEW_W = 600;
const VIEW_H = 400;
const MARGIN = { left: 42, right: 12, top: 12, bottom: 26 };
const SAMPLES = 300;

function escapeXml(str) {
    return ('' + str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function niceStep(range, targetTicks) {
    const raw = range / targetTicks;
    const pow = Math.pow(10, Math.floor(Math.log10(raw)));
    const norm = raw / pow;
    if (norm <= 1) return pow;
    if (norm <= 2) return 2 * pow;
    if (norm <= 5) return 5 * pow;
    return 10 * pow;
}

function formatTick(value, step) {
    const decimals = Math.max(0, -Math.floor(Math.log10(step)));
    let text = value.toFixed(Math.min(decimals, 6));
    if (text === '-0') text = '0';
    return text;
}

// 2D curves stretch with the element box ('none'); 3D surfaces pass
// 'xMidYMid meet' so the projected cube is never distorted — the preview,
// the board element and exports all show the exact same geometry.
function svgHeader(preserveAspectRatio) {
    return (
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' +
        VIEW_W +
        ' ' +
        VIEW_H +
        '" width="100%" height="100%" preserveAspectRatio="' +
        (preserveAspectRatio || 'none') +
        '">'
    );
}

function placeholderSvg(expression) {
    return (
        svgHeader() +
        '<rect x="1" y="1" width="' +
        (VIEW_W - 2) +
        '" height="' +
        (VIEW_H - 2) +
        '" fill="none" stroke="#bbbbbb" stroke-width="2"/>' +
        '<text x="' +
        VIEW_W / 2 +
        '" y="' +
        VIEW_H / 2 +
        '" text-anchor="middle" font-size="20" fill="#888888">' +
        escapeXml(expression || '?') +
        '</text></svg>'
    );
}

// el: {expression, xMin, xMax, color, weight, yMin?, yMax?, azimuth?, elevation?}.
// Returns an SVG string (Canvg-safe: only path/line/rect/polygon/text with inline
// attributes). Expressions using y render as a 3D surface; otherwise as the 2D
// curve. The 2D curve uses currentColor so callers apply el.color on a wrapper
// (same pattern as embeddedSvg elements); the 3D surface bakes shaded fills
// derived from el.color directly into the SVG string.
functionPlot.buildFunctionGraphSvg = function (el) {
    let root;
    try {
        root = functionPlot.parse(el.expression);
    } catch (err) {
        return placeholderSvg(el.expression);
    }
    if (usesY(root)) {
        return buildSurfaceSvg(el, root);
    }
    return buildCurveSvg(el, root);
};

function buildCurveSvg(el, root) {
    let xMin = Number.isFinite(el.xMin) ? el.xMin : -10;
    let xMax = Number.isFinite(el.xMax) ? el.xMax : 10;
    if (xMin >= xMax) {
        xMin = -10;
        xMax = 10;
    }

    const plotX = MARGIN.left;
    const plotY = MARGIN.top;
    const plotW = VIEW_W - MARGIN.left - MARGIN.right;
    const plotH = VIEW_H - MARGIN.top - MARGIN.bottom;

    const xs = [];
    const ys = [];
    for (let i = 0; i <= SAMPLES; i++) {
        const x = xMin + ((xMax - xMin) * i) / SAMPLES;
        let y;
        try {
            y = functionPlot.evaluate(root, x);
        } catch (err) {
            y = NaN;
        }
        xs.push(x);
        ys.push(typeof y === 'number' ? y : NaN);
    }

    // Robust auto y-range: 5th-95th percentile of finite samples, so tan/1 over x
    // asymptote blow-ups do not flatten the interesting part of the curve. When
    // the full range is not much larger than the robust window (no blow-up),
    // use the full range so ordinary curves render without any clipping.
    const finite = ys.filter((y) => Number.isFinite(y)).sort((a, b) => a - b);
    let yMin, yMax;
    if (finite.length === 0) {
        yMin = -1;
        yMax = 1;
    } else {
        yMin = finite[Math.floor((finite.length - 1) * 0.05)];
        yMax = finite[Math.ceil((finite.length - 1) * 0.95)];
        const fullMin = finite[0];
        const fullMax = finite[finite.length - 1];
        if (fullMax - fullMin <= 3 * (yMax - yMin)) {
            yMin = fullMin;
            yMax = fullMax;
        }
        if (yMax - yMin < 1e-9) {
            yMin -= 1;
            yMax += 1;
        }
        const pad = (yMax - yMin) * 0.1;
        yMin -= pad;
        yMax += pad;
        // Pull in y=0 when it is close, so the x-axis is usually visible.
        const span = yMax - yMin;
        if (yMin > 0 && yMin < span * 1.5) yMin = 0;
        if (yMax < 0 && -yMax < span * 1.5) yMax = 0;
    }

    const toPx = (x) => plotX + ((x - xMin) / (xMax - xMin)) * plotW;
    const toPy = (y) => plotY + ((yMax - y) / (yMax - yMin)) * plotH;

    let parts = [svgHeader()];

    // Grid and tick labels.
    const xStep = niceStep(xMax - xMin, 6);
    const yStep = niceStep(yMax - yMin, 5);
    for (let gx = Math.ceil(xMin / xStep) * xStep; gx <= xMax + 1e-9; gx += xStep) {
        const px = toPx(gx);
        parts.push(
            '<line x1="' + px + '" y1="' + plotY + '" x2="' + px + '" y2="' + (plotY + plotH) +
            '" stroke="#bbbbbb" stroke-width="0.5" opacity="0.5"/>'
        );
        parts.push(
            '<text x="' + px + '" y="' + (plotY + plotH + 16) +
            '" text-anchor="middle" font-size="11" fill="#888888">' + formatTick(gx, xStep) + '</text>'
        );
    }
    for (let gy = Math.ceil(yMin / yStep) * yStep; gy <= yMax + 1e-9; gy += yStep) {
        const py = toPy(gy);
        parts.push(
            '<line x1="' + plotX + '" y1="' + py + '" x2="' + (plotX + plotW) + '" y2="' + py +
            '" stroke="#bbbbbb" stroke-width="0.5" opacity="0.5"/>'
        );
        parts.push(
            '<text x="' + (plotX - 6) + '" y="' + (py + 4) +
            '" text-anchor="end" font-size="11" fill="#888888">' + formatTick(gy, yStep) + '</text>'
        );
    }

    // Axes: at 0 when inside the range, otherwise on the plot edge.
    const axisY = yMin <= 0 && yMax >= 0 ? toPy(0) : plotY + plotH;
    const axisX = xMin <= 0 && xMax >= 0 ? toPx(0) : plotX;
    parts.push(
        '<line x1="' + plotX + '" y1="' + axisY + '" x2="' + (plotX + plotW) + '" y2="' + axisY +
        '" stroke="#666666" stroke-width="1"/>'
    );
    parts.push(
        '<line x1="' + axisX + '" y1="' + plotY + '" x2="' + axisX + '" y2="' + (plotY + plotH) +
        '" stroke="#666666" stroke-width="1"/>'
    );

    // Curve: only what lies inside the plot box is drawn. Segments leaving or
    // entering the box are clipped at the exact boundary crossing (no clamped
    // stubs at the start/end of the line); non-finite samples and
    // asymptote-sized jumps lift the pen instead of joining the branches.
    const yTop = plotY;
    const yBottom = plotY + plotH;
    let d = '';
    let penDown = false;
    let prev = null;
    const pushMove = (x, y) => {
        d += 'M' + x.toFixed(2) + ' ' + y.toFixed(2);
    };
    const pushLine = (x, y) => {
        d += 'L' + x.toFixed(2) + ' ' + y.toFixed(2);
    };
    for (let i = 0; i <= SAMPLES; i++) {
        if (!Number.isFinite(ys[i])) {
            penDown = false;
            prev = null;
            continue;
        }
        const px = toPx(xs[i]);
        const py = toPy(ys[i]);
        const inside = py >= yTop && py <= yBottom;
        if (prev === null || Math.abs(py - prev.py) > 2 * plotH) {
            penDown = false;
            if (inside) {
                pushMove(px, py);
                penDown = true;
            }
            prev = { px, py };
            continue;
        }
        const prevInside = prev.py >= yTop && prev.py <= yBottom;
        if (prevInside && inside) {
            if (!penDown) {
                pushMove(prev.px, prev.py);
                penDown = true;
            }
            pushLine(px, py);
        } else if (prevInside && !inside) {
            // Leaving the box: draw up to the boundary crossing, then lift.
            const boundary = py < yTop ? yTop : yBottom;
            const t = (boundary - prev.py) / (py - prev.py);
            if (!penDown) {
                pushMove(prev.px, prev.py);
            }
            pushLine(prev.px + t * (px - prev.px), boundary);
            penDown = false;
        } else if (!prevInside && inside) {
            // Entering the box: start at the boundary crossing.
            const boundary = prev.py < yTop ? yTop : yBottom;
            const t = (boundary - prev.py) / (py - prev.py);
            pushMove(prev.px + t * (px - prev.px), boundary);
            pushLine(px, py);
            penDown = true;
        } else {
            // Both endpoints outside: only draw the chunk crossing the box.
            if ((prev.py < yTop && py > yBottom) || (prev.py > yBottom && py < yTop)) {
                const tTop = (yTop - prev.py) / (py - prev.py);
                const tBottom = (yBottom - prev.py) / (py - prev.py);
                const tIn = Math.min(tTop, tBottom);
                const tOut = Math.max(tTop, tBottom);
                pushMove(prev.px + tIn * (px - prev.px), prev.py < yTop ? yTop : yBottom);
                pushLine(prev.px + tOut * (px - prev.px), prev.py < yTop ? yBottom : yTop);
            }
            penDown = false;
        }
        prev = { px, py };
    }
    if (d) {
        parts.push(
            '<path d="' + d + '" fill="none" stroke="currentColor" stroke-width="' +
            1.5 * (el.weight || 1) + '" stroke-linejoin="round"/>'
        );
    }

    parts.push('</svg>');
    return parts.join('');
}

// ---------------------------------------------------------------------------
// 3D surface rendering (z = f(x,y))
// ---------------------------------------------------------------------------

const SURFACE_GRID = 24; // 25x25 samples -> 576 quads, ~100 KB SVG
const DEFAULT_AZIMUTH = -60; // degrees, matplotlib-like default view
const DEFAULT_ELEVATION = 30;
const DEFAULT_COLOR = { r: 63, g: 81, b: 181 };

function parseHexColor(color) {
    if (typeof color !== 'string') return DEFAULT_COLOR;
    let m = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(color.trim());
    if (m) {
        return { r: parseInt(m[1] + m[1], 16), g: parseInt(m[2] + m[2], 16), b: parseInt(m[3] + m[3], 16) };
    }
    m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(color.trim());
    if (m) {
        return { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) };
    }
    return DEFAULT_COLOR;
}

function rgbString(r, g, b) {
    return 'rgb(' + Math.round(r) + ',' + Math.round(g) + ',' + Math.round(b) + ')';
}

function buildSurfaceSvg(el, root) {
    let xMin = Number.isFinite(el.xMin) ? el.xMin : -10;
    let xMax = Number.isFinite(el.xMax) ? el.xMax : 10;
    if (xMin >= xMax) {
        xMin = -10;
        xMax = 10;
    }
    let yMin = Number.isFinite(el.yMin) ? el.yMin : -10;
    let yMax = Number.isFinite(el.yMax) ? el.yMax : 10;
    if (yMin >= yMax) {
        yMin = -10;
        yMax = 10;
    }
    const azimuth = Number.isFinite(el.azimuth) ? el.azimuth : DEFAULT_AZIMUTH;
    let elevation = Number.isFinite(el.elevation) ? el.elevation : DEFAULT_ELEVATION;
    elevation = Math.max(0, Math.min(90, elevation));

    const plotX = MARGIN.left;
    const plotY = MARGIN.top;
    const plotW = VIEW_W - MARGIN.left - MARGIN.right;
    const plotH = VIEW_H - MARGIN.top - MARGIN.bottom;

    // Sample z on the grid.
    const zGrid = [];
    for (let i = 0; i <= SURFACE_GRID; i++) {
        const row = [];
        const x = xMin + ((xMax - xMin) * i) / SURFACE_GRID;
        for (let j = 0; j <= SURFACE_GRID; j++) {
            const y = yMin + ((yMax - yMin) * j) / SURFACE_GRID;
            let z;
            try {
                z = functionPlot.evaluate(root, x, y);
            } catch (err) {
                z = NaN;
            }
            row.push(typeof z === 'number' ? z : NaN);
        }
        zGrid.push(row);
    }

    // Robust z-range: same percentile approach as the 2D curve, so asymptote
    // blow-ups do not flatten the surface.
    const finite = [];
    for (const row of zGrid) for (const z of row) if (Number.isFinite(z)) finite.push(z);
    finite.sort((a, b) => a - b);
    let zMin, zMax;
    if (finite.length === 0) {
        zMin = -1;
        zMax = 1;
    } else {
        zMin = finite[Math.floor((finite.length - 1) * 0.05)];
        zMax = finite[Math.ceil((finite.length - 1) * 0.95)];
        const fullMin = finite[0];
        const fullMax = finite[finite.length - 1];
        if (fullMax - fullMin <= 3 * (zMax - zMin)) {
            zMin = fullMin;
            zMax = fullMax;
        }
        if (zMax - zMin < 1e-9) {
            zMin -= 1;
            zMax += 1;
        }
        const pad = (zMax - zMin) * 0.1;
        zMin -= pad;
        zMax += pad;
    }

    // Normalize to the unit cube; finite out-of-range z clamps to a plateau
    // (no giant polygons), non-finite z stays NaN and punches a hole.
    const toUnit = (i, j) => {
        const z = zGrid[i][j];
        return {
            u: i / SURFACE_GRID - 0.5,
            v: j / SURFACE_GRID - 0.5,
            w: Number.isFinite(z) ? Math.max(-0.5, Math.min(0.5, (z - zMin) / (zMax - zMin) - 0.5)) : NaN,
        };
    };

    // Orthographic camera: azimuth rotates about the vertical (z/w) axis,
    // elevation tilts the view down onto the surface.
    const a = (azimuth * Math.PI) / 180;
    const e = (elevation * Math.PI) / 180;
    const sinA = Math.sin(a);
    const cosA = Math.cos(a);
    const sinE = Math.sin(e);
    const cosE = Math.cos(e);
    const project = (p) => ({
        sx: -p.u * sinA + p.v * cosA,
        sy: -p.u * sinE * cosA - p.v * sinE * sinA + p.w * cosE,
        depth: p.u * cosE * cosA + p.v * cosE * sinA + p.w * sinE,
    });

    // Fit the unit cube (not the samples) into the plot box, so the framing
    // stays stable while the view rotates.
    let bMinX = Infinity, bMaxX = -Infinity, bMinY = Infinity, bMaxY = -Infinity;
    for (const cu of [-0.5, 0.5]) {
        for (const cv of [-0.5, 0.5]) {
            for (const cw of [-0.5, 0.5]) {
                const c = project({ u: cu, v: cv, w: cw });
                bMinX = Math.min(bMinX, c.sx);
                bMaxX = Math.max(bMaxX, c.sx);
                bMinY = Math.min(bMinY, c.sy);
                bMaxY = Math.max(bMaxY, c.sy);
            }
        }
    }
    const scale = Math.min(plotW / (bMaxX - bMinX), plotH / (bMaxY - bMinY));
    const centerX = plotX + plotW / 2;
    const centerY = plotY + plotH / 2;
    const bCenterX = (bMinX + bMaxX) / 2;
    const bCenterY = (bMinY + bMaxY) / 2;
    const toScreen = (s) => ({
        px: centerX + (s.sx - bCenterX) * scale,
        py: centerY - (s.sy - bCenterY) * scale,
    });

    // Quads with Lambert shading from el.color; painter's algorithm (far to near).
    const base = parseHexColor(el.color);
    const lightLen = Math.sqrt(0.4 * 0.4 + 0.3 * 0.3 + 0.85 * 0.85);
    const light = { x: 0.4 / lightLen, y: -0.3 / lightLen, z: 0.85 / lightLen };
    const meshColor = rgbString(base.r * 0.4, base.g * 0.4, base.b * 0.4);
    const quads = [];
    for (let i = 0; i < SURFACE_GRID; i++) {
        for (let j = 0; j < SURFACE_GRID; j++) {
            const corners = [toUnit(i, j), toUnit(i + 1, j), toUnit(i + 1, j + 1), toUnit(i, j + 1)];
            if (corners.some((c) => !Number.isFinite(c.w))) continue;

            // Surface normal from the quad diagonals (world space).
            const d1 = {
                x: corners[2].u - corners[0].u,
                y: corners[2].v - corners[0].v,
                z: corners[2].w - corners[0].w,
            };
            const d2 = {
                x: corners[3].u - corners[1].u,
                y: corners[3].v - corners[1].v,
                z: corners[3].w - corners[1].w,
            };
            const n = {
                x: d1.y * d2.z - d1.z * d2.y,
                y: d1.z * d2.x - d1.x * d2.z,
                z: d1.x * d2.y - d1.y * d2.x,
            };
            const nLen = Math.sqrt(n.x * n.x + n.y * n.y + n.z * n.z) || 1;
            const intensity = Math.abs((n.x * light.x + n.y * light.y + n.z * light.z) / nLen);

            const projected = corners.map((c) => project(c));
            const points = projected
                .map((s) => {
                    const p = toScreen(s);
                    return p.px.toFixed(1) + ',' + p.py.toFixed(1);
                })
                .join(' ');
            const depth = (projected[0].depth + projected[1].depth + projected[2].depth + projected[3].depth) / 4;

            const shade = (ch) => {
                const lit = ch + (255 - ch) * 0.5;
                const dark = ch * 0.45;
                return dark + (lit - dark) * intensity;
            };
            quads.push({
                depth,
                svg:
                    '<polygon points="' + points + '" fill="' + rgbString(shade(base.r), shade(base.g), shade(base.b)) +
                    '" stroke="' + meshColor + '" stroke-width="0.6" stroke-linejoin="round"/>',
            });
        }
    }
    quads.sort((q1, q2) => q1.depth - q2.depth);

    let parts = [svgHeader('xMidYMid meet')];
    for (const q of quads) parts.push(q.svg);

    // Axes: three cube edges from the (-,-,-) corner, drawn over the surface.
    const origin = { u: -0.5, v: -0.5, w: -0.5 };
    const axes = [
        { end: { u: 0.5, v: -0.5, w: -0.5 }, label: 'x', min: xMin, max: xMax },
        { end: { u: -0.5, v: 0.5, w: -0.5 }, label: 'y', min: yMin, max: yMax },
        { end: { u: -0.5, v: -0.5, w: 0.5 }, label: 'z', min: zMin, max: zMax },
    ];
    const o = toScreen(project(origin));
    for (const axis of axes) {
        const p = toScreen(project(axis.end));
        parts.push(
            '<line x1="' + o.px.toFixed(1) + '" y1="' + o.py.toFixed(1) + '" x2="' + p.px.toFixed(1) +
            '" y2="' + p.py.toFixed(1) + '" stroke="#666666" stroke-width="1"/>'
        );
        // Axis letter slightly beyond the end, min/max value at the end.
        const dx = p.px - o.px;
        const dy = p.py - o.py;
        const len = Math.sqrt(dx * dx + dy * dy) || 1;
        const lx = p.px + (dx / len) * 14;
        const ly = p.py + (dy / len) * 14;
        parts.push(
            '<text x="' + lx.toFixed(1) + '" y="' + ly.toFixed(1) +
            '" text-anchor="middle" font-size="12" fill="#888888">' + axis.label + '</text>'
        );
        const step = niceStep(axis.max - axis.min, 4);
        parts.push(
            '<text x="' + (p.px + (dx / len) * 28).toFixed(1) + '" y="' + (p.py + (dy / len) * 28 + 4).toFixed(1) +
            '" text-anchor="middle" font-size="11" fill="#888888">' + formatTick(axis.max, step) + '</text>'
        );
    }

    parts.push('</svg>');
    return parts.join('');
}

export default functionPlot;
